// The "N new" count next to the Leads menu item (lead-forms SPEC §D.1, §D.8).
// navConfig is a static array, so the badge fetches its own number: on mount,
// on every route change, every 60 s while the tab is visible, and when the
// inbox announces a status change (window event 'leads:changed'). It renders
// nothing while loading, at 0 and on any error (403 included): no error UI in
// the menu, and no localStorage (the count is the server's). NavItem's
// RoleBasedGuard keeps it from mounting for roles that cannot see the item.
// Design round 6: the chip is the brand colour (a ghost primary Label). The
// count is the inbox's work queue (leads still at status New), not an error or
// an alert, so it no longer borrows the error red.
// SPEC-P P.3.1: the badge is `new + updated` (leads a repeat or a fix updated
// since staff last opened them; the BE never counts a lead in both), with the
// split in the tooltip. An older BE sends no `updated`: it counts as 0.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Tooltip } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Label from 'src/components/Label';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

const POLL_MS = 60 * 1000;

export const LEADS_CHANGED_EVENT = 'leads:changed';

export type BadgeCounts = { new: number; updated: number; total: number };

const num = (v: unknown): number =>
  typeof v === 'number' && isFinite(v) && v > 0 ? Math.floor(v) : 0;

/** GET leads/summary → the badge numbers (`updated` absent → 0). */
export function badgeCounts(body: unknown): BadgeCounts {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const n = num(b.new);
  const u = num(b.updated);
  return { new: n, updated: u, total: n + u };
}

/** "3 new, 2 updated" (the tooltip; "3 new" when nothing was updated). */
export const badgeTip = (c: BadgeCounts): string =>
  c.updated ? `${c.new} new, ${c.updated} updated` : `${c.new} new`;

export default function LeadsNavBadge() {
  const { pathname } = useLocation();
  const [counts, setCounts] = useState<BadgeCounts | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get(APIS.LEAD_FORMS.LEADS_SUMMARY);
      const body = res.data?.data ?? res.data;
      if (alive.current) setCounts(badgeCounts(body));
    } catch {
      if (alive.current) setCounts(null);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Mount and every route change.
  useEffect(() => {
    load();
  }, [pathname, load]);

  // The 60 s poll runs only while the tab is visible; coming back refreshes at once.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(LEADS_CHANGED_EVENT, load);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(LEADS_CHANGED_EVENT, load);
    };
  }, [load]);

  const count = counts ? counts.total : 0;
  if (!counts || count <= 0) return null;
  return (
    <Tooltip title={badgeTip(counts)}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Label color="primary">
          {count > 999 ? '999+' : count}
          <Box component="span" sx={visuallyHidden}>
            {` (${badgeTip(counts)})`}
          </Box>
        </Label>
      </Box>
    </Tooltip>
  );
}
